/**
 * faultTreeEngine.js
 * Fault tree analysis engine with minimal cut set and quantification algorithms
 */

import { weibullCDF, exponentialCDF } from './distributions.js';

/**
 * Factorial helper function
 */
function factorial(n) {
  if (n < 0) return NaN;
  if (n === 0 || n === 1) return 1;
  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  return result;
}

/**
 * Binomial coefficient helper: C(n, k)
 */
function binomialCoeff(n, k) {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  let result = 1;
  for (let i = 0; i < k; i++) {
    result *= (n - i) / (i + 1);
  }
  return result;
}

/**
 * Fault Tree Engine
 * Supports AND, OR, VOTING (k-of-n), INHIBIT, PRIORITY_AND, XOR gates
 * and Basic, Undeveloped, House, Conditional, External event types
 */
export class FaultTreeEngine {
  constructor(gates = {}, primaryEvents = {}, failureModels = {}) {
    this.gates = gates; // gate_id -> {type, inputs: [gate/event ids]}
    this.primaryEvents = primaryEvents; // event_id -> {type, distribution, eta, beta, ...}
    this.failureModels = failureModels; // Precomputed failure probabilities
    this.results = null;
  }

  /**
   * Find minimal cut sets using MOCUS algorithm
   * Returns array of cut sets, each cut set is an array of basic event IDs
   */
  findMinimalCutSets(topGateId) {
    if (!this.gates[topGateId]) {
      throw new Error(`Gate ${topGateId} not found`);
    }

    // Start with top gate
    let cutSets = [this._expandGate(topGateId)];

    // Iteratively reduce to minimal cut sets
    for (let iteration = 0; iteration < 100; iteration++) {
      let newCutSets = [];
      let changed = false;

      for (const cutSet of cutSets) {
        const reduced = this._reduceCutSet(cutSet);
        if (JSON.stringify(reduced) !== JSON.stringify(cutSet)) {
          changed = true;
        }
        newCutSets.push(reduced);
      }

      cutSets = newCutSets;

      // Remove duplicates and supersets
      cutSets = this._removeRedundantSets(cutSets);

      if (!changed) break;
    }

    return cutSets;
  }

  /**
   * Expand a gate into its constituent events/gates
   */
  _expandGate(gateId) {
    const gate = this.gates[gateId];
    if (!gate) return [gateId]; // Primary event

    if (gate.type === 'OR') {
      // OR: union of all inputs
      const result = [];
      for (const input of gate.inputs || []) {
        const expanded = this._expandGate(input);
        result.push(...expanded);
      }
      return result;
    }

    if (gate.type === 'AND') {
      // AND: Cartesian product of inputs (each combination required)
      let result = [[]];
      for (const input of gate.inputs || []) {
        const expanded = this._expandGate(input);
        const newResult = [];
        for (const set of result) {
          for (const item of expanded) {
            newResult.push([...set, item]);
          }
        }
        result = newResult;
      }
      return result;
    }

    if (gate.type === 'VOTING') {
      // k-of-n: combinations of k inputs
      const k = gate.k || 2;
      const inputs = gate.inputs || [];
      const combinations = this._combinations(inputs, k);
      return combinations;
    }

    if (gate.type === 'PRIORITY_AND') {
      // Priority AND: inputs must fail in sequence (left to right)
      let result = [[]];
      for (const input of gate.inputs || []) {
        const expanded = this._expandGate(input);
        const newResult = [];
        for (const set of result) {
          for (const item of expanded) {
            newResult.push([...set, item]);
          }
        }
        result = newResult;
      }
      return result;
    }

    if (gate.type === 'INHIBIT') {
      // INHIBIT gate: AND with conditioning event
      // First input is basic event, second is conditioning event
      const inputs = gate.inputs || [];
      if (inputs.length !== 2) {
        console.warn('INHIBIT gate should have exactly 2 inputs');
      }
      let result = [[]];
      for (const input of inputs) {
        const expanded = this._expandGate(input);
        const newResult = [];
        for (const set of result) {
          for (const item of expanded) {
            newResult.push([...set, item]);
          }
        }
        result = newResult;
      }
      return result;
    }

    if (gate.type === 'XOR') {
      // XOR: exactly one input fails
      const inputs = gate.inputs || [];
      // For XOR, each individual input can cause failure
      const result = [];
      for (const input of inputs) {
        const expanded = this._expandGate(input);
        result.push(...expanded);
      }
      return result;
    }

    return [gateId];
  }

  /**
   * Get combinations of size k from array
   */
  _combinations(arr, k) {
    if (k === 1) return arr.map(x => [x]);
    if (k === arr.length) return [arr];
    if (k > arr.length) return [];

    const result = [];
    for (let i = 0; i < arr.length; i++) {
      const head = arr[i];
      const tail = arr.slice(i + 1);
      const rest = this._combinations(tail, k - 1);
      for (const r of rest) {
        result.push([head, ...r]);
      }
    }
    return result;
  }

  /**
   * Reduce a cut set by removing redundant gates
   */
  _reduceCutSet(cutSet) {
    // If all elements are primary events, return as is
    const allPrimary = cutSet.every(id => !this.gates[id]);
    if (allPrimary) return cutSet;

    // Try to expand gates and simplify
    const expanded = [];
    for (const item of cutSet) {
      if (this.gates[item]) {
        const exp = this._expandGate(item);
        expanded.push(...(Array.isArray(exp[0]) ? exp : [exp]));
      } else {
        expanded.push(item);
      }
    }

    return expanded.length > 0 ? expanded : cutSet;
  }

  /**
   * Remove duplicate and superset cut sets
   */
  _removeRedundantSets(cutSets) {
    const unique = [];
    const seen = new Set();

    for (const set of cutSets) {
      const sorted = Array.isArray(set[0]) ? set : [set];
      const key = JSON.stringify(sorted.map(s => Array.isArray(s) ? s.sort() : s).sort());

      if (!seen.has(key)) {
        seen.add(key);

        // Check if this is a superset of an existing set
        let isSuperset = false;
        for (const other of unique) {
          if (this._isSubset(other, set)) {
            isSuperset = true;
            break;
          }
        }

        if (!isSuperset) {
          // Remove any existing sets that are supersets of this one
          unique.splice(
            0,
            unique.length,
            ...unique.filter(other => !this._isSuperset(set, other))
          );
          unique.push(set);
        }
      }
    }

    return unique;
  }

  _isSubset(a, b) {
    const aFlat = a.flat();
    const bFlat = b.flat();
    return aFlat.every(x => bFlat.includes(x)) && aFlat.length <= bFlat.length;
  }

  _isSuperset(a, b) {
    return this._isSubset(b, a);
  }

  /**
   * Quantify top event probability using rare event approximation
   */
  quantify(topGateId, missionTime) {
    if (!this.gates[topGateId]) {
      throw new Error(`Gate ${topGateId} not found`);
    }

    const _topGate = this.gates[topGateId];

    // Compute event probabilities first
    const eventProbs = new Map();
    for (const eventId in this.primaryEvents) {
      const event = this.primaryEvents[eventId];
      eventProbs.set(eventId, this._computeEventProbability(event, missionTime));
    }

    // Use Inclusion-Exclusion principle for top gate
    const minimalCutSets = this.findMinimalCutSets(topGateId);
    let topEventProb = 0;

    if (minimalCutSets.length === 0) {
      return 0;
    }

    // Rare event approximation: P(top) ≈ sum(P(cut sets)) for independent events
    for (const cutSet of minimalCutSets) {
      let cutSetProb = 1;
      const flatSet = cutSet.flat();
      for (const eventId of flatSet) {
        cutSetProb *= eventProbs.get(eventId) || 0.001;
      }
      topEventProb += cutSetProb;
    }

    // Inclusion-Exclusion correction for small probabilities
    if (minimalCutSets.length > 1) {
      // Second-order correction
      for (let i = 0; i < minimalCutSets.length; i++) {
        for (let j = i + 1; j < minimalCutSets.length; j++) {
          const set1 = minimalCutSets[i].flat();
          const set2 = minimalCutSets[j].flat();
          const intersection = set1.filter(x => set2.includes(x));

          if (intersection.length > 0) {
            let intersectionProb = 1;
            for (const eventId of intersection) {
              intersectionProb *= eventProbs.get(eventId) || 0.001;
            }
            topEventProb -= intersectionProb;
          }
        }
      }
    }

    return Math.max(0, Math.min(1, topEventProb));
  }

  /**
   * Compute individual event probability at time t
   */
  _computeEventProbability(event, missionTime) {
    if (event.type === 'House') {
      return 1; // Always occurs
    }

    if (event.type === 'Undeveloped') {
      return event.probability || 0.001; // Use provided or small default
    }

    if (event.type === 'Basic') {
      // Use distribution to compute CDF at missionTime
      const dist = (event.distribution || 'exponential').toLowerCase();

      switch (dist) {
        case 'weibull':
          return weibullCDF(
            missionTime,
            event.eta || 1000,
            event.beta || 1,
            event.gamma || 0
          );
        case 'exponential':
          return exponentialCDF(missionTime, event.lambda || 0.001);
        case 'normal':
          return this._normalCDF(missionTime, event.mu || 1000, event.sigma || 100);
        case 'lognormal':
          return this._lognormalCDF(missionTime, event.mu || 6, event.sigma || 1);
        default:
          return event.probability || 0.001;
      }
    }

    if (event.type === 'External') {
      return event.probability || 0.01; // External events have fixed probability
    }

    return 0;
  }

  /**
   * Normal CDF approximation
   */
  _normalCDF(x, mu = 0, sigma = 1) {
    const z = (x - mu) / sigma;
    const p = 0.2316419;
    const b1 = 0.319381530;
    const b2 = -0.356563782;
    const b3 = 1.781477937;
    const b4 = -1.821255978;
    const b5 = 1.330274429;

    const t = 1.0 / (1.0 + p * Math.abs(z));
    const pdf = (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * z * z);
    const cdf =
      1 - pdf * (b1 * t + b2 * t * t + b3 * t * t * t + b4 * t * t * t * t + b5 * t * t * t * t * t);
    return z >= 0 ? cdf : 1 - cdf;
  }

  /**
   * Lognormal CDF
   */
  _lognormalCDF(x, mu, sigma) {
    if (x <= 0) return 0;
    return this._normalCDF(Math.log(x), mu, sigma);
  }

  /**
   * Compute priority AND gate probability
   * Inputs must fail in sequence (left to right)
   * P(priority_AND) = product(P(Ai)) / n!
   */
  priorityAndProbability(inputProbs) {
    if (inputProbs.length === 0) return 0;
    let product = 1;
    for (const p of inputProbs) {
      product *= p;
    }
    const n = inputProbs.length;
    return product / factorial(n);
  }

  /**
   * Compute INHIBIT gate probability
   * One input is basic event, other is conditioning event
   * P(INHIBIT) = P(basic) × P(condition)
   */
  inhibitProbability(basicEventProb, conditioningEventProb) {
    return basicEventProb * conditioningEventProb;
  }

  /**
   * Compute XOR gate probability
   * Exactly one input must fail
   * P(XOR) = Σ P(Ai) × Π(1 - P(Aj)) for j≠i
   */
  xorProbability(inputProbs) {
    if (inputProbs.length === 0) return 0;
    if (inputProbs.length === 1) return inputProbs[0];

    let result = 0;
    for (let i = 0; i < inputProbs.length; i++) {
      let term = inputProbs[i];
      for (let j = 0; j < inputProbs.length; j++) {
        if (i !== j) {
          term *= (1 - inputProbs[j]);
        }
      }
      result += term;
    }
    return result;
  }

  /**
   * Alpha-factor CCF (Common Cause Failure) model
   * Given alpha parameters (α1, α2, ... αk) for group of k components
   * Qt = Σ(αi × C(k-1, i-1) / C(k, i) × Qi)
   * where Qi is i-th order failure probability
   */
  alphaFactorCCF(alphas, basicEventProb) {
    const k = alphas.length;
    let result = 0;

    for (let i = 1; i <= k; i++) {
      const alpha = alphas[i - 1];
      const comb1 = binomialCoeff(k - 1, i - 1);
      const comb2 = binomialCoeff(k, i);
      const ratio = (comb1 / comb2) || 1;

      // Qi is approximated as basicEventProb^i
      const qi = Math.pow(basicEventProb, i);
      result += alpha * ratio * qi;
    }

    return Math.min(1, Math.max(0, result));
  }

  /**
   * Multiple Greek Letter (MGL) CCF model
   * Given β, γ, δ parameters, compute group failure probability
   * Q2 = β × Q
   * Q3 = β × γ × Q
   * Q4 = β × γ × δ × Q, etc.
   */
  mglCCF(beta, gamma, delta, basicEventProb, groupSize) {
    if (groupSize === 1) return basicEventProb;
    if (groupSize === 2) return beta * basicEventProb;
    if (groupSize === 3) return beta * gamma * basicEventProb;
    if (groupSize === 4) return beta * gamma * delta * basicEventProb;

    // For larger groups, extend the pattern
    let result = basicEventProb;
    const factors = [beta, gamma, delta];
    for (let i = 1; i < groupSize && i < factors.length; i++) {
      result *= factors[i];
    }
    return Math.min(1, Math.max(0, result));
  }

  /**
   * Barlow-Proschan importance measure
   * I_BP(i) = integral of [∂h/∂pi × fi(t)] dt / F_sys
   * Approximated as: Birnbaum importance × component failure density
   */
  barlowProschanImportance(eventId, topGateId, missionTime) {
    const event = this.primaryEvents[eventId];
    if (!event) return 0;

    // Get Birnbaum importance (partial derivative)
    const minimalCutSets = this.findMinimalCutSets(topGateId);
    const eventProbs = new Map();
    for (const eId in this.primaryEvents) {
      eventProbs.set(eId, this._computeEventProbability(this.primaryEvents[eId], missionTime));
    }

    let probWith1 = 0;
    let probWith0 = 0;

    for (const cutSet of minimalCutSets) {
      const flatSet = cutSet.flat();
      if (flatSet.includes(eventId)) {
        probWith1 += this._computeCutSetProbability(flatSet, missionTime, new Map([[eventId, 1]]));
        probWith0 += this._computeCutSetProbability(flatSet, missionTime, new Map([[eventId, 0]]));
      }
    }

    const birnbaum = Math.abs(probWith1 - probWith0);

    // Approximate failure density (PDF) at mission time
    const pdfApprox = this._computeEventDensity(event, missionTime);

    // System failure probability
    const systemFailureProb = this.quantify(topGateId, missionTime);

    return (birnbaum * pdfApprox) / Math.max(systemFailureProb, 1e-10);
  }

  /**
   * Approximate event failure density (PDF) at time t
   */
  _computeEventDensity(event, missionTime) {
    const dist = (event.distribution || 'exponential').toLowerCase();
    const dt = 0.001; // Small time increment for numerical derivative

    const cdfT = this._computeEventProbability(event, missionTime);
    const cdfT_plus = this._computeEventProbability(event, missionTime + dt);

    return (cdfT_plus - cdfT) / dt;
  }

  /**
   * Sequential importance measure
   * Probability that component i is the last to fail in a cut set
   */
  sequentialImportance(eventId, topGateId, missionTime) {
    const minimalCutSets = this.findMinimalCutSets(topGateId);
    const eventProbs = new Map();
    for (const eId in this.primaryEvents) {
      eventProbs.set(eId, this._computeEventProbability(this.primaryEvents[eId], missionTime));
    }

    let sequentialProb = 0;
    let cutSetsWithEvent = 0;

    for (const cutSet of minimalCutSets) {
      const flatSet = cutSet.flat();
      if (flatSet.includes(eventId)) {
        cutSetsWithEvent++;

        // Probability that all other events in cut set fail before eventId
        let probLastToFail = 1;
        for (const otherId of flatSet) {
          if (otherId !== eventId) {
            const otherProb = eventProbs.get(otherId) || 0.001;
            probLastToFail *= otherProb;
          }
        }

        sequentialProb += probLastToFail * eventProbs.get(eventId);
      }
    }

    return cutSetsWithEvent > 0 ? sequentialProb / cutSetsWithEvent : 0;
  }

  /**
   * Compute importance measures for each basic event
   */
  computeImportance(topGateId, missionTime) {
    const minimalCutSets = this.findMinimalCutSets(topGateId);
    const baselineProbability = this.quantify(topGateId, missionTime);

    const importance = new Map();

    // Get all basic events
    const allEvents = Object.keys(this.primaryEvents);

    for (const eventId of allEvents) {
      const event = this.primaryEvents[eventId];
      const _eventProb = this._computeEventProbability(event, missionTime);

      // Birnbaum importance: partial derivative of P_top with respect to event probability
      // Approximate as (P_top_with_P=1 - P_top_with_P=0) / (P_top(1) - P_top(0))
      let probWith1 = 0;
      let probWith0 = 0;

      for (const cutSet of minimalCutSets) {
        const flatSet = cutSet.flat();
        if (flatSet.includes(eventId)) {
          // This cut set includes the event
          probWith1 += this._computeCutSetProbability(flatSet, missionTime, new Map([[eventId, 1]]));
          probWith0 += this._computeCutSetProbability(flatSet, missionTime, new Map([[eventId, 0]]));
        }
      }

      const birnbaum = Math.abs(probWith1 - probWith0);

      // Fussell-Vesely: fraction of top event probability in cut sets containing this event
      let relevantProb = 0;
      for (const cutSet of minimalCutSets) {
        if (cutSet.flat().includes(eventId)) {
          relevantProb += this._computeCutSetProbability(cutSet.flat(), missionTime);
        }
      }
      const fussellVesely = baselineProbability > 0 ? Math.min(1, relevantProb / baselineProbability) : 0;

      // Risk Achievement Worth (RAW) = P_top(component failed) / P_top(baseline)
      // P_top(q=1) = baseline minus relevant cut sets at baseline + relevant cut sets with event=1
      const pTopFailed = (baselineProbability - relevantProb) + probWith1;
      const raw = baselineProbability > 0 ? pTopFailed / baselineProbability : 0;

      // Risk Reduction Worth (RRW) = P_top(baseline) / P_top(component perfect)
      // P_top(q=0) = baseline minus relevant cut sets (they contribute 0 when event is perfect)
      const pTopPerfect = Math.max(1e-12, baselineProbability - relevantProb + probWith0);
      const rrw = pTopPerfect > 0 ? baselineProbability / pTopPerfect : 0;

      // Barlow-Proschan importance
      const barlowProschan = this.barlowProschanImportance(eventId, topGateId, missionTime);

      // Sequential importance
      const sequential = this.sequentialImportance(eventId, topGateId, missionTime);

      importance.set(eventId, {
        id: eventId,
        birnbaum: Math.max(0, Math.min(1, birnbaum)),
        fussellVesely: Math.max(0, Math.min(1, fussellVesely)),
        riskAchievementWorth: Math.max(0, raw),
        riskReductionWorth: Math.max(0, rrw),
        barlowProschan: Math.max(0, Math.min(1, barlowProschan)),
        sequential: Math.max(0, Math.min(1, sequential)),
      });
    }

    return Array.from(importance.values());
  }

  /**
   * Compute probability of a cut set
   */
  _computeCutSetProbability(cutSet, missionTime, overrides = new Map()) {
    let prob = 1;
    for (const eventId of cutSet) {
      if (overrides.has(eventId)) {
        prob *= overrides.get(eventId);
      } else {
        const event = this.primaryEvents[eventId];
        if (event) {
          prob *= this._computeEventProbability(event, missionTime);
        }
      }
    }
    return prob;
  }

  /**
   * Get results object
   */
  getResults() {
    if (!this.results) {
      throw new Error('Call quantify() or computeImportance() first');
    }
    return this.results;
  }

  /**
   * Full analysis on top gate
   */
  analyze(topGateId, missionTime) {
    const minimalCutSets = this.findMinimalCutSets(topGateId);
    const topEventProb = this.quantify(topGateId, missionTime);
    const importanceMeasures = this.computeImportance(topGateId, missionTime);

    const eventProbs = new Map();
    for (const eventId in this.primaryEvents) {
      const event = this.primaryEvents[eventId];
      eventProbs.set(eventId, this._computeEventProbability(event, missionTime));
    }

    const cutSetProbs = minimalCutSets.map(cs => ({
      cutSet: cs.flat ? cs.flat() : cs,
      probability: this._computeCutSetProbability(cs.flat ? cs.flat() : cs, missionTime),
    }));

    this.results = {
      topEventProbability: topEventProb,
      minimalCutSets: minimalCutSets.map(cs => cs.flat ? cs.flat() : cs),
      cutSetProbabilities: cutSetProbs,
      importanceMeasures,
      eventProbabilities: Array.from(eventProbs.entries()).map(([id, prob]) => ({
        id,
        probability: prob,
      })),
    };

    return this.results;
  }
}

export default FaultTreeEngine;
